import React, { useState, useEffect } from 'react';
import LoadingAnimation from './LoadingAnimation';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { useNavigate } from 'react-router-dom';
import {
  LineChart, Line, XAxis, YAxis, Tooltip,
  ResponsiveContainer, ReferenceLine,
} from 'recharts';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ── Humor config ───────────────────────────────────────────────────────────
const HUMOR_OPTIONS = [
  { score: 5, label: 'Muito bem',     hex: '#4A7C59' },
  { score: 4, label: 'Bem',           hex: '#7FB069' },
  { score: 3, label: 'Mais ou menos', hex: '#F5A623' },
  { score: 2, label: 'Mal',           hex: '#D97706' },
  { score: 1, label: 'Muito mal',     hex: '#B45309' },
];

const ESTADO_TO_SCORE = {
  leve_e_presente: 5, tranquila: 4, neutra: 3, sobrecarregada: 2, pesada: 1,
};

const GATILHO_LABELS = {
  fome: '🍽️ Fome', sono: '😴 Sono', exaustao: '📋 Sobrecarga',
  tensao: '😤 Tensão', ciclo: '🌙 Ciclo', isolamento: '🫥 Isolamento',
  conflito: '⚡ Conflito',
};

const humorConfig = (score) =>
  HUMOR_OPTIONS.find(o => o.score === score) || HUMOR_OPTIONS[2];

const resolveScore = (c) =>
  c.humor_score || ESTADO_TO_SCORE[c.estado] || null;

// ── Custom tooltip for the chart ───────────────────────────────────────────
const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const score = payload[0]?.value;
  const cfg   = humorConfig(Math.round(score));
  return (
    <div className="bg-white border border-gray-200 rounded-xl px-3 py-2 shadow text-sm">
      <p className="text-gray-500 text-xs mb-0.5">{label}</p>
      <p className="font-semibold" style={{ color: cfg.hex }}>{cfg.label}</p>
    </div>
  );
};

// ── Entry card ─────────────────────────────────────────────────────────────
const EntryCard = ({ checkin }) => {
  const score = resolveScore(checkin);
  const cfg   = humorConfig(score);
  const gatilhosAtivos = Object.keys(GATILHO_LABELS).filter(k => checkin[`gatilho_${k}`]);
  const [expanded, setExpanded] = useState(false);

  const hasDepth = checkin.situacao_texto || checkin.necessidade_texto || gatilhosAtivos.length > 0;

  return (
    <div className="border-l-2 pl-4 py-1" style={{ borderColor: cfg.hex }}>
      {/* Date + humor */}
      <div className="flex items-center gap-2 mb-1">
        <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: cfg.hex }} />
        <span className="text-xs text-gray-400">{checkin.data_registro}</span>
        <span className="text-xs font-semibold" style={{ color: cfg.hex }}>{cfg.label}</span>
        {hasDepth && (
          <button
            onClick={() => setExpanded(!expanded)}
            className="ml-auto text-xs text-gray-400 hover:text-gray-600"
          >
            {expanded ? 'menos' : 'mais'}
          </button>
        )}
      </div>

      {/* Depth content */}
      {expanded && hasDepth && (
        <div className="mt-2 space-y-2 text-sm text-gray-700">
          {checkin.situacao_texto && (
            <p className="bg-gray-50 rounded-lg px-3 py-2 italic text-gray-600 text-xs">
              "{checkin.situacao_texto}"
            </p>
          )}
          {checkin.necessidade_texto && (
            <p className="text-xs text-purple-700">
              <span className="font-medium">Necessidade: </span>{checkin.necessidade_texto}
            </p>
          )}
          {gatilhosAtivos.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {gatilhosAtivos.map(k => (
                <span key={k} className="text-xs bg-orange-50 text-orange-700 border border-orange-200 rounded-full px-2 py-0.5">
                  {GATILHO_LABELS[k]}
                </span>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ── Main ───────────────────────────────────────────────────────────────────
const EmotionDiaryHistory = () => {
  const navigate  = useNavigate();
  const [checkins, setCheckins] = useState([]);
  const [loading,  setLoading]  = useState(true);

  useEffect(() => {
    axios.get(`${API}/emotion-checkin/history?limit=60`, { withCredentials: true })
      .then(r => setCheckins((r.data.checkins || []).filter(c => resolveScore(c) !== null)))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-purple-50 to-white">
        <LoadingAnimation size="sm" />
      </div>
    );
  }

  // ── Chart data: last 30 days ─────────────────────────────────────────────
  const today  = new Date();
  const days30 = Array.from({ length: 30 }, (_, i) => {
    const d = new Date(today);
    d.setDate(d.getDate() - (29 - i));
    return d.toISOString().slice(0, 10);
  });

  // One entry per day (first non-skip of the day)
  const byDay = {};
  checkins.forEach(c => {
    const d = c.data_registro;
    if (!byDay[d]) byDay[d] = c;
  });

  const chartData = days30.map(date => ({
    date: date.slice(5),     // MM-DD for display
    fullDate: date,
    score: byDay[date] ? resolveScore(byDay[date]) : null,
  }));

  // Entries sorted newest first
  const sorted = [...checkins].sort((a, b) =>
    (b.data_registro || '').localeCompare(a.data_registro || '')
  );

  // Group by month/week for timeline
  const grouped = {};
  sorted.forEach(c => {
    const d = new Date(c.data_registro || c.created_at || '');
    const key = isNaN(d) ? 'Outros' : d.toLocaleString('pt-BR', { month: 'long', year: 'numeric' });
    (grouped[key] = grouped[key] || []).push(c);
  });

  const empty = checkins.length === 0;

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50 to-white pb-16">
      <div className="max-w-2xl mx-auto px-4 py-8">

        {/* Header */}
        <div className="flex items-center justify-between mb-7">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Histórico de humor</h1>
            <p className="text-sm text-gray-500 mt-0.5">
              {checkins.length} registro{checkins.length !== 1 ? 's' : ''}
            </p>
          </div>
          <Button
            size="sm"
            className="bg-purple-600 hover:bg-purple-700 text-white"
            onClick={() => navigate('/dashboard')}
          >
            Novo check-in
          </Button>
        </div>

        {empty ? (
          /* ── Empty state ── */
          <Card className="p-10 text-center">
            <p className="text-4xl mb-4">🌱</p>
            <h2 className="text-lg font-semibold text-gray-900 mb-2">
              Ainda não há registros
            </h2>
            <p className="text-sm text-gray-500 mb-5">
              Quando você registrar como está, os dados vão aparecer aqui.
            </p>
            <Button
              className="bg-purple-600 hover:bg-purple-700 text-white"
              onClick={() => navigate('/dashboard')}
            >
              Fazer o primeiro check-in
            </Button>
          </Card>
        ) : (
          <>
            {/* ── Mood chart ── */}
            <Card className="p-5 mb-6">
              <p className="text-sm font-semibold text-gray-700 mb-4">
                Últimos 30 dias
              </p>
              <ResponsiveContainer width="100%" height={160}>
                <LineChart data={chartData} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 10, fill: '#9ca3af' }}
                    tickLine={false}
                    axisLine={false}
                    interval={6}
                  />
                  <YAxis
                    domain={[1, 5]}
                    ticks={[1, 2, 3, 4, 5]}
                    tick={{ fontSize: 10, fill: '#9ca3af' }}
                    tickLine={false}
                    axisLine={false}
                  />
                  <Tooltip content={<ChartTooltip />} />
                  <ReferenceLine y={3} stroke="#e5e7eb" strokeDasharray="4 2" />
                  <Line
                    type="monotone"
                    dataKey="score"
                    stroke="#7F77DD"
                    strokeWidth={2}
                    dot={(props) => {
                      const { cx, cy, payload } = props;
                      if (payload.score === null) return null;
                      const cfg = humorConfig(Math.round(payload.score));
                      return (
                        <circle
                          key={payload.fullDate}
                          cx={cx} cy={cy} r={4}
                          fill={cfg.hex} stroke="white" strokeWidth={1.5}
                        />
                      );
                    }}
                    connectNulls={false}
                  />
                </LineChart>
              </ResponsiveContainer>

              {/* Scale legend */}
              <div className="flex justify-between mt-3 px-1">
                {HUMOR_OPTIONS.slice().reverse().map(o => (
                  <div key={o.score} className="flex items-center gap-1">
                    <div className="w-2 h-2 rounded-full" style={{ backgroundColor: o.hex }} />
                    <span className="text-xs text-gray-400">{o.label}</span>
                  </div>
                ))}
              </div>
            </Card>

            {/* ── Timeline ── */}
            <div className="space-y-7">
              {Object.entries(grouped).map(([month, entries]) => (
                <div key={month}>
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">
                    {month}
                  </p>
                  <div className="space-y-3">
                    {entries.map((c, i) => (
                      <EntryCard key={c.checkin_id || i} checkin={c} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default EmotionDiaryHistory;
